import * as React from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  InputLabelProps as MuiInputLabelProps,
} from '@material-ui/core';
import { SelectProps as MuiSelectProps } from '@material-ui/core/Select';
import Select from '../Select';

import { useFormControlStyles, useHelperStyles, useLabelStyles } from './selectFieldStyles';

type SelectFieldVariant = 'default' | 'reversed';

export interface SelectFieldProps {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  id?: string;
  label?: string;
  name?: string;
  native?: boolean;
  onChange?: (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBlur?: React.FocusEventHandler<any>;
  value?: string;
  variant?: SelectFieldVariant;
  InputLabelProps?: MuiInputLabelProps;
  SelectProps?: MuiSelectProps;
}

/**
 * Use `Select` for rendering a select, see stories for more complete examples. This is a wrapper for Select
 * that includes FormControl, InputLabel, and FormHelperText. To disable native select, set native to false.
 *
 * ```js
 * import { SelectField } from 'ui';
 * ```
 */
const SelectField: React.FunctionComponent<SelectFieldProps> = ({
  children,
  className,
  disabled,
  error,
  fullWidth,
  helperText,
  id,
  label,
  name,
  native = true,
  onBlur,
  onChange,
  value,
  variant = 'default',
  InputLabelProps = {},
  SelectProps,
}) => {
  const labelStyles = useLabelStyles();
  const formControlStyles = useFormControlStyles({ fullWidth });

  const labelClasses = labelStyles;
  const helperTextClasses = useHelperStyles();
  const formControlClasses = formControlStyles;
  const width = fullWidth ? '100%' : 'auto';

  const inputLabel = React.useRef<HTMLLabelElement>(null);
  const [labelWidth, setLabelWidth] = React.useState(0);

  React.useEffect(() => {
    setLabelWidth(inputLabel?.current?.offsetWidth ?? 0);
  }, []);

  return (
    <FormControl
      classes={formControlClasses}
      className={className}
      disabled={disabled}
      error={error}
      style={{ width }}
      variant="filled"
    >
      {label && (
        <InputLabel
          {...InputLabelProps}
          classes={labelClasses}
          htmlFor={id ? id : SelectProps?.inputProps?.id}
        >
          {label}
        </InputLabel>
      )}
      <Select
        inputProps={SelectProps?.inputProps}
        className={SelectProps?.className}
        disabled={disabled ?? SelectProps?.disabled}
        id={id ?? SelectProps?.inputProps?.id}
        label={label}
        labelWidth={labelWidth}
        name={name ?? SelectProps?.inputProps?.name}
        native={native}
        onChange={onChange ?? SelectProps?.onChange}
        onBlur={onBlur ?? SelectProps?.onBlur}
        startAdornment={SelectProps?.startAdornment}
        value={value ?? SelectProps?.value}
      >
        {children}
      </Select>
      {helperText && <FormHelperText classes={helperTextClasses}>{helperText}</FormHelperText>}
    </FormControl>
  );
};
export default SelectField;
